import { useCallback } from 'react';
import getIn from '../components/connectedForm/utils/getIn';
import Operations from './operations';
import checkReadOnly from '../components/connectedForm/utils/checkReadOnly';
import { EntityType } from '../@types/entityTypes';
import { IFullUserDetails } from '../graphql/schema/p4types';
import { useSelector } from 'react-redux';
import { IReduxStore } from '../@types/reduxTypes';

export const ALLOW_VIEW = 1;
export const ALLOW_ADD = 2;
export const ALLOW_EDIT = 4;
export const ALLOW_DELETE = 8;

const permissionDefault = false; //Default behaviour (When no permission match is found).

function getActionMap(userActions) {
  const tempObj = {};
  for (const action of userActions) {
    tempObj[action.action.name] = {
      permitted: action.permitted,
      children: action.action.children,
    };
  }
  return tempObj;
}

export const checkOperationCreate = (props: Parameters<typeof checkOperation>[0]) => {
  if (!props) return permissionDefault;
  const entity = props.entity;
  if (entity && entity.operations && !(entity.operations & ALLOW_ADD)) {
    return false; //Operations are configured and don't include create, therefore should not be available.
  }
  return checkOperation(props, Operations.Create);
};

export function checkOperationEdit(props) {
  if (props.forceReadOnly) return false; //Use for things like modals, etc
  if (props.ignoreUpdatePermission) return true; //E.g. 'Allocate Stock' (Because if you have access to the screen, makes no sense to not be able to edit).

  const identity = getIn(props, 'match.params.id') || '';
  const path = getIn(props, 'match.path') || '';
  const { entity } = props;
  let actionType;
  if (
    (typeof identity === 'string' && identity.toUpperCase() === 'NEW') ||
    path.endsWith('Template') ||
    props.addCapabilityOnly
  ) {
    //NEW or Template. (Template forms are always 'new' - if editing, regular forms would be used)
    if (entity.operations && !(entity.operations & ALLOW_ADD)) {
      return false; //Operations are configured and don't include create, therefore should not be available.
    }
    actionType = Operations.Create;
  } else {
    if (entity.operations && !(entity.operations & ALLOW_EDIT)) {
      return false; //Operations are configured and don't include edit, therefore should not be available.
    }
    actionType = Operations.Update;
  }

  return checkOperation(props, actionType);
}

export function checkOperationDelete(props) {
  const { entity, values, forceReadOnly } = props;

  if (forceReadOnly) return false; //Use for things like modals, etc
  const completeStatus = !values || Object.keys(values).length === 0 || checkReadOnly(entity.readOnlyStatus, values);
  if (entity.operations && !(entity.operations & ALLOW_DELETE)) {
    return false; //Operations are configured and don't include delete, therefore should not be available.
  }
  return checkOperation(props, Operations.Delete) && !completeStatus;
}

export function checkAction(
  {
    screenReference: screenReferenceFromProps,
    entity,
    app,
  }: { screenReference?: string; entity?: EntityType; app?: any } = {},
  actionButton
) {
  if (!app || !actionButton) return permissionDefault;
  if (actionButton.ignorePermissions) return true;

  if (actionButton.actionReference) {
    return checkPermission({ app }, actionButton.actionReference);
  }
  return checkOperation(
    { screenReference: screenReferenceFromProps, entity, app },
    actionButton.screenReference || actionButton.description
  );
}

export function checkOperation(
  {
    screenReference: screenReferenceFromProps,
    entity,
    app,
  }: { screenReference?: string; entity?: EntityType; app: { fullUserDetails?: IFullUserDetails } },
  actionType: Operations
) {
  if (!actionType) return permissionDefault;

  const screenReference = screenReferenceFromProps || entity?.screenReference;
  if (!screenReference) {
    console.warn(
      'Screen Reference is not defined',
      { screenReference: screenReferenceFromProps, entity, app },
      actionType
    );
  }
  if (Array.isArray(screenReference)) {
    //If we find an array, then 'every' child reference is required.
    return screenReference.every((scReference) => {
      const permissionReference = `${scReference}_${actionType.toUpperCase()}`;
      return checkPermission({ app }, permissionReference);
    });
  }
  const permissionReference = `${screenReference}_${actionType.toUpperCase()}`;
  return checkPermission({ app }, permissionReference);
}

/**
 * Check if at least one of the given list of permissions is available. (Used for menu display, e.g. Navbar 'Fast Add').
 */
export function checkAnyPermission(
  { app }: Parameters<typeof checkPermission>[0],
  permissionReference: Array<string> = []
) {
  return permissionReference.some((value) => {
    return checkPermission({ app }, value);
  });
}

/**
 * @deprecated - Use 'useCheckPermission' hook instead (Which internally uses this, for the moment).
 *             - (Wish to discourage passing of general "app" context when all we really need is fullUserDetails).
 */
export function checkPermission({ app }: { app: { fullUserDetails?: IFullUserDetails } }, permissionReference: string) {
  const user = app?.fullUserDetails;

  if (user && user.systemUser && user.systemUser.admin) {
    //System Admin.
    return true;
  } else if (!user || user.loading || !user.companyRoleActions || !permissionReference) {
    return permissionDefault;
  }

  const action = getActionMap(user.companyRoleActions)[permissionReference];

  if (!action) {
    console.warn('Permission configuration missing for action.', permissionReference);
  }

  return !!action && action.permitted;
}

/**
 *  Get a function for checking if the specified permission is allowed for the currently logged in user.
 */
export const useCheckPermission = () => {
  // Intentionally uses redux selector directly rather than useFullUserDetails - Issue with dependency loops if importing hooks.ts to this file.
  const fullUserDetails = useSelector((store: IReduxStore) => store.app.fullUserDetails);
  return useCallback(
    (permissionReference: string) =>
      checkPermission({ app: { fullUserDetails: fullUserDetails } }, permissionReference),
    [fullUserDetails]
  );
};
